import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Fade,
  Slide,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  ShoppingBag,
  RateReview,
  AdminPanelSettings,
  Logout,
  Person,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  // Весь фронтенд, кроме /login, теперь внутренний admin-инструмент - пункты меню
  // показываем только авторизованному админу, остальным они всё равно недоступны.
  const navigationItems = isAuthenticated && user?.role === 'admin'
    ? [
        { label: 'Главная', path: '/', icon: <Dashboard /> },
        { label: 'Продукты', path: '/products', icon: <ShoppingBag /> },
        { label: 'Отзывы', path: '/reviews', icon: <RateReview /> },
        { label: 'Админ панель', path: '/admin', icon: <AdminPanelSettings /> },
      ]
    : [];

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}
    >
      <Toolbar sx={{ py: 1 }}>
        <Fade in timeout={500}>
          <Typography
            variant="h5"
            component="div"
            sx={{ 
              flexGrow: 1, 
              cursor: 'pointer',
              fontWeight: 800,
              color: '#1a1a1a',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
            onClick={() => navigate('/')}
          >
            Reviews Pro
          </Typography>
        </Fade>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {navigationItems.map((item, index) => (
            <Slide direction="down" in timeout={500 + index * 100} key={item.path}>
              <Button
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  background: location.pathname === item.path 
                    ? 'rgba(0, 0, 0, 0.1)' 
                    : 'transparent',
                  color: '#1a1a1a',
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(0, 0, 0, 0.05)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {item.label}
              </Button>
            </Slide>
          ))}
        </Box>
        
        {isAuthenticated ? (
          <Box sx={{ ml: 3 }}>
            <Slide direction="left" in timeout={800}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<Person />}
                  label={user?.username}
                  sx={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    color: '#1a1a1a',
                    fontWeight: 600,
                  }}
                />
                <IconButton
                  size="large"
                  onClick={handleMenu}
                  sx={{
                    color: '#1a1a1a',
                    background: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.1)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <AccountCircle />
                </IconButton>
              </Box>
            </Slide>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  mt: 1,
                },
              }}
            >
              <MenuItem onClick={handleClose} sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {user?.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.role === 'admin' ? 'Администратор' : 'Пользователь'}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Logout sx={{ color: 'error.main' }} />
                  <Typography color="error.main" sx={{ fontWeight: 600 }}>
                    Выйти
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Slide direction="left" in timeout={800}>
            <Button 
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                background: '#1a1a1a',
                color: '#ffffff',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: '#333333',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Войти
            </Button>
          </Slide>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;